import axios from 'axios';
import { authService } from '@/features/auth/model/auth-service';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';

type RetryableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };

export const apiClient = axios.create({
	baseURL: import.meta.env.PROD ? import.meta.env.API_URL : undefined,
	headers: {
		'Content-Type': 'application/json',
	},
});

// Request interceptor: Inject auth token
apiClient.interceptors.request.use(
	async (config: InternalAxiosRequestConfig) => {
		const token = await authService.getAccessToken();
		if (token && config.headers) {
			config.headers.Authorization = `Bearer ${token}`;
		}
		return config;
	},
	(error) => Promise.reject(error)
);

// Single-flight refresh: concurrent 401s share one acquireTokenSilent call instead of
// hammering the token endpoint N times. Cleared in .finally so a transient refresh
// failure doesn't permanently break the refresh path for the session.
let pendingRefresh: Promise<string | null> | null = null;

function refreshAccessToken(): Promise<string | null> {
	if (!pendingRefresh) {
		pendingRefresh = authService.forceRefreshAccessToken().finally(() => {
			pendingRefresh = null;
		});
	}
	return pendingRefresh;
}

// Response interceptor: on 401, force-refresh once and replay. If that fails (or the
// replay also 401s), clear the MSAL cache and re-enter loginRedirect. Never call
// logoutRedirect here — that bounces through Microsoft's "You signed out" screen,
// which is wrong UX for an expired token the user didn't ask to invalidate.
apiClient.interceptors.response.use(
	(response) => response,
	async (error: AxiosError) => {
		const originalConfig = error.config as RetryableRequestConfig | undefined;

		if (error.response?.status !== 401 || !originalConfig || originalConfig._retry) {
			if (error.response?.status === 401) {
				// Either the replay itself 401'd or we have no config to retry with —
				// silent recovery is impossible, fall through to interactive re-login.
				await authService.reauthenticate();
			}
			return Promise.reject(error);
		}

		originalConfig._retry = true;

		const newToken = await refreshAccessToken();
		if (!newToken) {
			await authService.reauthenticate();
			return Promise.reject(error);
		}

		// Set the header explicitly as a safety net — the request interceptor will run
		// again and overwrite this with getAccessToken(), but only `if (token)`. If that
		// returns null for some edge reason, our explicit value survives.
		originalConfig.headers = originalConfig.headers ?? {};
		(originalConfig.headers as Record<string, string>).Authorization = `Bearer ${newToken}`;

		return apiClient.request(originalConfig);
	}
);

// Custom mutator for Orval v8 with axios client
export const apiInstance = async <T>(
	config: {
		url: string;
		method: string;
		params?: any;
		data?: any;
		headers?: Record<string, string>;
		signal?: AbortSignal;
	},
	options?: any
): Promise<T> => {
	const response = await apiClient.request<T>({
		...config,
		signal: config.signal || undefined,
		headers: {
			'Content-Type': 'application/json',
			...(options?.headers || {}),
		},
	});

	return response.data;
};
